/**
 * AlertsAdmin.tsx — Admin > Account > Account Alerts.
 *
 * The corporate dashboard's Account Alerts page (components/account-alerts-
 * table.tsx) for the signed-in account: the cards this practice shows on its
 * own dashboard, to the user levels it picks, on an optional schedule. In the
 * Lead Sources layout (components/admin/AdminList.tsx):
 *   - Title (its content under it), User Level, Start Date and Stop Date (on
 *     the account's own clock), A–Z by title until a column is clicked; search
 *     by title or banner URL; Show deleted;
 *   - "Add alert" top right; a click on a live row opens it; Edit / Delete on a
 *     live row, Restore on a deleted one;
 *   - the add / edit form is corp's dialog, with the live card preview
 *     (AccountAlertFormModal).
 * Group-wide announcements Prospera or a corporate admin posts to every
 * practice (broadcasts) are theirs to manage and are not listed. Corp's
 * Account filter, per-title account grouping and compare tool are
 * multi-account: left out. Writes need Admin Access.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live alert still opens, as a viewer
 * (AccountAlertFormModal).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AccountAlertFormModal, PACIFIC_TZ, storedToLocal } from '@/components/admin/AccountAlertFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort } from '@/components/admin/useListSort';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useTimezones } from '@/hooks/useTimezones';
import { richTextToPlain } from '@/lib/alertHeroStyle';
import { getAccountDetails } from '@/services/accountDetailsAdmin.service';
import { deleteAccountAlert, listAccountAlerts, restoreAccountAlert, type AccountAlert } from '@/services/accountAlerts.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'userLevel', header: 'User Level', className: 'w-[14%]', sortable: true },
  { key: 'start', header: 'Start Date', className: 'w-[14%]', sortable: true },
  { key: 'stop', header: 'Stop Date', className: 'w-[14%]', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

export const USER_LEVEL_LABELS: Record<number, string> = {
  0: 'All Users',
  1: 'Level 1+',
  2: 'Level 2+',
  3: 'Level 3+',
  4: 'Level 4+',
  5: 'Level 5+',
};

const titleOf = (a: AccountAlert) => richTextToPlain(a.alertTitle) || `Alert #${a.accountAlertID}`;

/** "Oct 8, 2026" on the account's clock, or — for none. */
function formatDay(stored: string, tz: string): string {
  const local = storedToLocal(stored, tz);
  if (!local) return '—';
  const [y, m, d] = local.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AlertsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [alerts, setAlerts] = useState<AccountAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AccountAlert | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AccountAlert | null>(null);
  const [timezoneId, setTimezoneId] = useState(0);
  const { data: timezones = [] } = useTimezones();
  // The account's own clock (aa_accounts.timezone); Pacific until it is known.
  const timezone = timezones.find((z) => z.value === timezoneId)?.tzName || PACIFIC_TZ;

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      // The account's own alerts; broadcasts are Prospera's / corp's.
      setAlerts((await listAccountAlerts(true)).filter((a) => !a.isBroadcast));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load account alerts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    getAccountDetails()
      .then((d) => setTimezoneId(d.timezone))
      .catch(() => setTimezoneId(0));
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      alerts.filter(
        (a) => (showDeleted || a.active) && (query === '' || titleOf(a).toLowerCase().includes(query) || a.alertURL.toLowerCase().includes(query)),
      ),
    [alerts, showDeleted, query],
  );
  const sorting = useListSort(rows, {
    initialKey: 'title',
    compare: {
      title: (a, b) => compareText(titleOf(a), titleOf(b)),
      userLevel: (a, b) => a.userLevel - b.userLevel,
      start: (a, b) => a.startDate.localeCompare(b.startDate),
      stop: (a, b) => a.stopDate.localeCompare(b.stopDate),
    },
  });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteAccountAlert(deleting.accountAlertID);
      toast.success('Alert deleted');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The alert could not be deleted.'));
    }
  }

  async function handleRestore(a: AccountAlert) {
    try {
      await restoreAccountAlert(a.accountAlertID);
      toast.success('Alert restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The alert could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Account Alerts"
      subtitle="Cards on this account's dashboard, shown to the user levels you choose, with optional dates."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add alert
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search alerts..."
          searchLabel="Search alerts"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="account-alerts-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading account alerts…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No alerts found' : 'No account alerts yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add an alert to show a card on your dashboard.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Account alerts" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((a) => {
              const deleted = !a.active;
              const name = titleOf(a);
              const preview = richTextToPlain(a.alertContent);
              return (
                <tr
                  key={a.accountAlertID}
                  data-row-id={a.accountAlertID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(a)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="alert-title">
                      {name}
                    </AdminRowName>
                    {preview && <div className="mt-0.5 max-w-xl truncate text-xs text-slate-400">{preview}</div>}
                  </td>
                  <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{USER_LEVEL_LABELS[a.userLevel] ?? `Level ${a.userLevel}+`}</td>
                  <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{formatDay(a.startDate, timezone)}</td>
                  <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{formatDay(a.stopDate, timezone)}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(a)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(a)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(a)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['alert', 'alerts']} />
        </>
      )}

      {editing && (
        <AccountAlertFormModal
          key={editing === 'new' ? 'new' : editing.accountAlertID}
          alert={editing === 'new' ? null : editing}
          timezone={timezone}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${titleOf(deleting)}"?` : ''}
        message="Its card leaves the dashboard. You can restore it from Show deleted."
        confirmLabel="Delete alert"
        variant="danger"
      />
    </AdminListPage>
  );
}

export default AlertsAdmin;
